import AssignmentTurnedInOutlinedIcon from '@mui/icons-material/AssignmentTurnedInOutlined';
import CategoryOutlinedIcon from '@mui/icons-material/CategoryOutlined';
import GridViewOutlinedIcon from '@mui/icons-material/GridViewOutlined';
import HubOutlinedIcon from '@mui/icons-material/HubOutlined';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Skeleton from '@mui/material/Skeleton';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { alpha, useTheme } from '@mui/material/styles';
import type { ReactNode } from 'react';
import {
  getSurfaceBorder,
  getSurfaceColor,
  radii,
} from '../../../theme/tokens';
import { useCatalogIndexMetrics } from './useCatalogIndexMetrics';

const MetricValue = ({
  loading,
  value,
}: {
  loading: boolean;
  value: number;
}) => {
  if (loading) {
    return (
      <Skeleton
        sx={{ borderRadius: 1.5, maxWidth: 76, transform: 'none' }}
        variant="rounded"
        width={76}
        height={30}
      />
    );
  }

  return (
    <Typography
      sx={{
        fontSize: { md: 30, xs: 26 },
        fontVariantNumeric: 'tabular-nums',
        fontWeight: 800,
        letterSpacing: '-0.02em',
        lineHeight: 1,
      }}
    >
      {Intl.NumberFormat('en-US').format(value)}
    </Typography>
  );
};

const CatalogOverviewCard = ({
  accent,
  helperText,
  icon,
  loading,
  title,
  value,
}: {
  accent: string;
  helperText: string;
  icon: ReactNode;
  loading: boolean;
  title: string;
  value: number;
}) => {
  const theme = useTheme();

  return (
    <Paper
      sx={{
        backgroundColor: getSurfaceColor(theme, 'raised'),
        backgroundImage: `linear-gradient(150deg, ${alpha(
          accent,
          theme.palette.mode === 'dark' ? 0.2 : 0.12,
        )} 0%, transparent 38%)`,
        border: `1px solid ${getSurfaceBorder(theme)}`,
        borderRadius: `${radii.xl}px`,
        boxShadow:
          theme.palette.mode === 'dark'
            ? '0 10px 22px rgba(0, 0, 0, 0.18)'
            : '0 6px 16px rgba(17, 34, 43, 0.05)',
        minWidth: 0,
        overflow: 'hidden',
        position: 'relative',
        px: { md: 2, xs: 1.75 },
        py: { md: 1.375, xs: 1.25 },
        '&::before': {
          background: `linear-gradient(90deg, ${accent} 0%, ${alpha(
            accent,
            0.28,
          )} 100%)`,
          content: '""',
          height: 2,
          left: 0,
          position: 'absolute',
          right: 0,
          top: 0,
        },
        '&::after': {
          background: `radial-gradient(circle, ${alpha(
            accent,
            theme.palette.mode === 'dark' ? 0.16 : 0.12,
          )} 0%, transparent 72%)`,
          content: '""',
          height: 72,
          pointerEvents: 'none',
          position: 'absolute',
          right: -22,
          top: -24,
          width: 72,
        },
      }}
    >
      <Stack spacing={0.875} sx={{ position: 'relative', zIndex: 1 }}>
        <Stack
          alignItems="center"
          direction="row"
          justifyContent="space-between"
          spacing={1}
        >
          <Box sx={{ minWidth: 0, pr: 0.75 }}>
            <Typography
              color="text.secondary"
              sx={{
                fontSize: 11,
                fontWeight: 700,
                letterSpacing: '0.05em',
                lineHeight: 1.2,
                textTransform: 'uppercase',
              }}
            >
              {title}
            </Typography>
          </Box>

          <Box
            sx={{
              alignItems: 'center',
              background: `linear-gradient(135deg, ${alpha(
                accent,
                0.18,
              )} 0%, ${alpha(accent, 0.08)} 100%)`,
              border: `1px solid ${alpha(accent, 0.16)}`,
              borderRadius: 1.5,
              color: accent,
              display: 'inline-flex',
              flexShrink: 0,
              height: 32,
              justifyContent: 'center',
              width: 32,
            }}
          >
            {icon}
          </Box>
        </Stack>

        <Box>
          <MetricValue loading={loading} value={value} />
        </Box>

        <Typography
          color="text.secondary"
          sx={{
            display: '-webkit-box',
            fontSize: 12,
            lineHeight: 1.3,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            WebkitBoxOrient: 'vertical',
            WebkitLineClamp: 1,
          }}
        >
          {helperText}
        </Typography>
      </Stack>
    </Paper>
  );
};

export const CatalogOverviewCards = () => {
  const theme = useTheme();
  const { error, loading, metrics } = useCatalogIndexMetrics();

  const cards = [
    {
      accent: theme.palette.primary.main,
      helperText: 'Components currently in the catalog',
      icon: <GridViewOutlinedIcon fontSize="small" />,
      title: 'All components',
      value: metrics.allComponents,
    },
    {
      accent: theme.palette.secondary.main,
      helperText: 'Owned by your identity groups',
      icon: <AssignmentTurnedInOutlinedIcon fontSize="small" />,
      title: 'Owned by me',
      value: metrics.ownedComponents,
    },
    {
      accent: '#0D8D58',
      helperText: 'Components marked production',
      icon: <CategoryOutlinedIcon fontSize="small" />,
      title: 'In production',
      value: metrics.productionComponents,
    },
    {
      accent: '#4A56D4',
      helperText: 'Systems and templates in Backstage',
      icon: <HubOutlinedIcon fontSize="small" />,
      title: 'Systems / templates',
      value: metrics.systems + metrics.templates,
    },
  ];

  return (
    <Box
      sx={{
        display: 'grid',
        gap: 2,
        gridTemplateColumns: {
          lg: 'repeat(4, minmax(0, 1fr))',
          md: 'repeat(2, minmax(0, 1fr))',
          xs: 'minmax(0, 1fr)',
        },
      }}
    >
      {cards.map(card => (
        <CatalogOverviewCard key={card.title} loading={loading} {...card} />
      ))}

      {error && (
        <Paper
          sx={{
            backgroundColor: getSurfaceColor(theme, 'raised'),
            border: `1px solid ${getSurfaceBorder(theme)}`,
            borderRadius: `${radii.xl}px`,
            boxShadow:
              theme.palette.mode === 'dark'
                ? '0 10px 22px rgba(0, 0, 0, 0.18)'
                : '0 6px 16px rgba(17, 34, 43, 0.05)',
            gridColumn: { lg: '1 / -1', xs: 'auto' },
            px: 2,
            py: 1.5,
          }}
        >
          <Typography color="text.secondary" sx={{ fontSize: 13 }}>
            Catalog overview metrics are temporarily unavailable.
          </Typography>
        </Paper>
      )}
    </Box>
  );
};
